import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Shield, FileCheck2, Calendar, User as UserIcon } from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { QUALITY_MANUAL } from '../data/qualityManual';

const P = SECTIONS.sops;

// Lightweight markdown → HTML for the subset we use: headings (###),
// bold, inline code, blockquote, unordered lists, ordered lists, tables,
// and paragraphs. No deps.
function renderMarkdown(md) {
  const lines = md.split('\n');
  const out = [];
  let i = 0;

  const inlineFormat = (s) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-muted text-[12px] font-mono">$1</code>');

  while (i < lines.length) {
    const line = lines[i];

    // Blank → spacer
    if (line.trim() === '') { i++; continue; }

    // H3
    if (line.startsWith('### ')) {
      out.push(`<h3 class="font-['Outfit'] text-base font-bold text-foreground mt-6 mb-2">${inlineFormat(line.slice(4))}</h3>`);
      i++; continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith('> ')) { buf.push(lines[i].slice(2)); i++; }
      out.push(`<blockquote class="border-l-4 pl-4 py-2 my-3 italic text-foreground/90" style="border-color: ${P.color}; background: ${P.color}0A">${inlineFormat(buf.join(' '))}</blockquote>`);
      continue;
    }

    // Table (pipe-based, with header separator row)
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?\s*:?-+/.test(lines[i + 1])) {
      const headerCells = line.split('|').map(s => s.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(lines[i].split('|').map(s => s.trim()).filter(Boolean));
        i++;
      }
      const thead = `<thead><tr>${headerCells.map(c => `<th class="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border">${inlineFormat(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map(r => `<tr class="border-b border-border/60">${r.map(c => `<td class="px-3 py-2 text-sm text-foreground align-top">${inlineFormat(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      out.push(`<div class="overflow-x-auto my-3"><table class="w-full border border-border rounded-lg overflow-hidden">${thead}${tbody}</table></div>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      out.push(`<ol class="list-decimal pl-6 my-3 space-y-1 text-sm text-foreground marker:text-[${P.color}] marker:font-semibold">${items.map(it => `<li>${inlineFormat(it)}</li>`).join('')}</ol>`);
      continue;
    }

    // Unordered list
    if (line.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(`<ul class="list-disc pl-6 my-3 space-y-1 text-sm text-foreground marker:text-[${P.color}]">${items.map(it => `<li>${inlineFormat(it)}</li>`).join('')}</ul>`);
      continue;
    }

    // Paragraph (collect consecutive non-empty, non-special lines)
    const paraBuf = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('### ') && !lines[i].startsWith('> ') && !lines[i].startsWith('- ') && !/^\d+\.\s/.test(lines[i]) && !lines[i].trim().startsWith('|')) {
      paraBuf.push(lines[i]); i++;
    }
    out.push(`<p class="text-sm text-foreground leading-relaxed my-2">${inlineFormat(paraBuf.join(' '))}</p>`);
  }

  return out.join('');
}

export default function QualityManualPage() {
  const navigate = useNavigate();
  const rendered = useMemo(() => QUALITY_MANUAL.sections.map(s => ({ ...s, html: renderMarkdown(s.body) })), []);
  const print = () => window.print();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Print-only <style> — makes the PDF look clean */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          .print-page-break { page-break-before: always; }
          h1, h2, h3 { color: #0E2D5C !important; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="quality-manual-page">
        {/* Back + print — hidden in print */}
        <div className="no-print flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/sops')}
            data-testid="qm-back"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button
            onClick={print}
            data-testid="qm-print"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white"
            style={{ backgroundColor: P.color }}
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
        </div>

        {/* Document cover */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-6" data-testid="qm-cover">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>
              <FileCheck2 className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>
              Quality Manual · v{QUALITY_MANUAL.version}
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">{QUALITY_MANUAL.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{QUALITY_MANUAL.org}</p>

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-xs">
            <Meta icon={Calendar} label="Effective" value={QUALITY_MANUAL.effectiveDate} />
            <Meta icon={Calendar} label="Review" value={QUALITY_MANUAL.reviewDate} />
            <Meta icon={UserIcon} label="Owner" value={QUALITY_MANUAL.owner} />
            <Meta icon={Shield} label="Governance" value={QUALITY_MANUAL.governanceLead} />
          </dl>
        </div>

        {/* Contents — anchor links */}
        <nav className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-6" data-testid="qm-toc">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">Contents</p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {QUALITY_MANUAL.sections.map(s => (
              <li key={s.id} className="text-sm">
                <a href={`#${s.id}`} className="flex items-center gap-2 text-foreground/85 hover:text-foreground transition-colors">
                  <span className="w-7 shrink-0 text-right font-mono text-[11px] text-muted-foreground">{s.n}.</span>
                  <span>{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Body */}
        <article className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="qm-body">
          {rendered.map((s) => (
            <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
              <h2 className="font-['Outfit'] text-xl font-bold text-foreground border-b border-border pb-2 mb-4 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>
                  {s.n}
                </span>
                {s.title}
              </h2>
              <div dangerouslySetInnerHTML={{ __html: s.html }} />
            </section>
          ))}

          {/* Signature block — useful for inspector-ready copies */}
          <div className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground grid grid-cols-2 gap-6">
            <div>
              <p className="uppercase tracking-wider text-[10px] font-bold">Approved by</p>
              <p className="mt-4 text-foreground">_________________________</p>
              <p className="mt-1 text-[11px]">Medical Director</p>
            </div>
            <div>
              <p className="uppercase tracking-wider text-[10px] font-bold">Date</p>
              <p className="mt-4 text-foreground">_________________________</p>
            </div>
          </div>
        </article>

        <p className="no-print text-center text-[11px] text-muted-foreground mt-6">
          Document owner: {QUALITY_MANUAL.owner} · {QUALITY_MANUAL.org}
        </p>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <dt className="flex items-center gap-1 uppercase tracking-wider text-[10px] font-bold text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </dt>
      <dd className="text-foreground mt-0.5 text-xs font-semibold">{value}</dd>
    </div>
  );
}
